import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { IonicPage, NavParams, ModalController, AlertController } from 'ionic-angular';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs';
import { IUser } from '../../models/user.model';
import { Store } from '@ngrx/store';
import { ENV } from '@app/env';
import { VotingData } from '../../providers/voting';
import { map } from 'rxjs/operators';
import { isEmpty, has } from 'lodash';
import { IVoting } from '../../models/voting.model';
import { UtilitiesProvider } from '../../providers/utilities';
import { VotingActiveComponent } from './components/voting-active/voting-active';
import { TranslateCodePipe } from '../../pipes/translate';
import { EventData } from '../../providers/event';

import * as userSelectors from '../../ngrx/reducers/auth.reducer';

@IonicPage({
  segment: 'voting'
})
@Component({
  selector: 'page-voting',
  templateUrl: 'voting.html',
})
export class VotingPage implements OnInit, OnDestroy {

  public moduleStateSubs: Subscription;
  public moduleState: boolean;
  private currentUserSubs: Subscription;
  private currentUser: IUser;
  public votingId: string;
  public activeVotingSubs: Subscription;
  public activeVotingInformation: IVoting;
  public votingInformationSubs: Subscription;
  public saveEventVotingStatisticAndViewSubs: Subscription;
  public canShowActiveEventVotingToUserSubs: Subscription;

  @Input('eventId')
  public eventId;

  constructor(
    private navParams: NavParams,
    private store: Store<{}>,
    private votingData: VotingData,
    private utilities: UtilitiesProvider,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private translatePipe: TranslateCodePipe,
    private eventData: EventData
  ) { }

  ngOnInit() {
    this.getCurrentUser();
  }

  public getCurrentUser() {
    this.currentUserSubs = this.store.select(userSelectors.getUser).subscribe((currentUser) => {
      if (currentUser) {
        this.currentUser = currentUser;
        this.getModuleState();
        this.getActiveVoting(currentUser.info.id);
      }
    })
  }

  public getClientId(): string {
    return ENV.client;
  }

  /**
   * return the clientId
   */
  public getEventId(): string {
    return this.navParams.get('eventId');
  }

  public getModuleState() {
    this.moduleStateSubs = this.eventData.getModuleState(ENV.client, this.getEventId(), 'voting').subscribe((moduleState => {
      if (moduleState) {
        this.moduleState = true;
      } else {
        this.moduleState = false;
      }
    }));

  }


  public getActiveVoting(userId: string) {
    this.activeVotingSubs = this.votingData.getEventActiveVoting(this.getClientId(), this.getEventId())
      .pipe(
        map(((activeVoting: string) => {
          this.utilities.consoleDecorator('activeVoting', activeVoting);
          if (!isEmpty(activeVoting)) {
            this.votingId = activeVoting;
            return true;
          }
          this.votingId = null;
          return Observable.of(false);
        })
        )
      ).subscribe((data) => {
        this.utilities.consoleDecorator('data', data);
        if (data === false) {
          this.utilities.consoleDecorator('No has an active voting now', '');
        } else {
          if (this.moduleState) {
            this.getVotingInformation(this.votingId);
          }
          this.utilities.consoleDecorator('Voting is active now', this.votingId);
        }
      });
  }

  /**
   * Get the voting information based on the votingId
   * @param votingId
   * @author Sebastián Guevara
   */
  public getVotingInformation(votingId: string) {
    this.votingInformationSubs = this.votingData.getVotingByClientId(this.getClientId(), votingId).subscribe((votingInformation: IVoting) => {
      this.utilities.consoleDecorator('votingInformation', votingInformation);
      if (votingInformation) {
        this.activeVotingInformation = votingInformation;
        this.canShowVotingModal();
      }
    })
  }

  /**
   * Verify if the user has an answer for the poll
   * 1. If the user no has answers, and the poll is alert, open a alert that says "New poll is Active",
   * if the user click on the open button will execute the method openModal
   * 2. If user has answer, do not do anything.
   * @author Sebastián Guevara
   */
  verifyUseAnswerByVoting(votingInformation: IVoting) {
    this.canShowActiveEventVotingToUserSubs = this.votingData.canShowActiveEventVotingToUser(this.getClientId(), this.getEventId(), votingInformation.id, this.currentUser.info.id).subscribe(async votingPermissions => {
      this.utilities.consoleDecorator('votinglPermissions', votingPermissions);
      if (votingPermissions.show) {
        if (votingInformation.general.isAlert) {
          this.alertCtrl.create({
            title: await this.translatePipe.transform('A new voting is active'),
            buttons: [
              {
                text: await this.utilities.translateTextOrObject('Open'),
                handler: () => {
                  this.openVotingModal();
                  this.saveEventVotingStatisticAndViewSubs = this.votingData.saveEventVotingStatisticAndView(this.getEventId(), this.activeVotingInformation.general.id, this.currentUser.info.id).subscribe(() => { });
                }
              },
              {
                text: await this.translatePipe.transform('Cancel'),
                handler: () => {
                  this.saveEventVotingStatisticAndViewSubs = this.votingData.saveEventVotingStatisticAndView(this.getEventId(), this.activeVotingInformation.general.id, this.currentUser.info.id).subscribe(() => { });
                }
              }
            ]
          }).present();
        }
      }
    })
  }


  /**
   * Open the votingActive Modal
   * @author Sebastián Guevara
   */
  public openVotingModal() {
    this.modalCtrl.create(VotingActiveComponent, {
      clientId: this.getClientId(),
      eventId: this.getEventId(),
      votingId: this.activeVotingInformation.general.id,
      ponderationType: this.activeVotingInformation.ponderation.type
    }, {
        enableBackdropDismiss: false
      }).present();
  }

  public canShowVotingModal() {
    if (has(this.activeVotingInformation, 'ponderation')) {
      switch (this.activeVotingInformation.ponderation.type) {
        case 'email':
          this.votingData.getVotingPonderationByUserId(ENV.client, this.activeVotingInformation.id, 'email', this.currentUser.info.id)
            .subscribe((canShow) => {
              if (canShow) {
                this.verifyUseAnswerByVoting(this.activeVotingInformation);
              }
            })
          break;
        case 'code':
          this.verifyUseAnswerByVoting(this.activeVotingInformation);
          break;
      }
    } else {
      this.utilities.consoleDecorator("The active voting do not have ponderation", this.activeVotingInformation);
    }

  }

  ngOnDestroy() {
    if (this.currentUserSubs) this.currentUserSubs.unsubscribe();
    if (this.moduleStateSubs) this.moduleStateSubs.unsubscribe();
    if (this.activeVotingSubs) this.activeVotingSubs.unsubscribe();
    if (this.votingInformationSubs) this.votingInformationSubs.unsubscribe();
    if (this.saveEventVotingStatisticAndViewSubs) this.saveEventVotingStatisticAndViewSubs.unsubscribe();
    if (this.canShowActiveEventVotingToUserSubs) this.canShowActiveEventVotingToUserSubs.unsubscribe();
  }


}



// WEBPACK FOOTER //
// ./src/pages/voting/voting.ts